export type TimezoneSource = 'system' | 'user' | 'browser' | 'phone_country';

/** A rejected device-zone save is retried this many times, then left alone. */
export const TIMEZONE_SYNC_ATTEMPT_LIMIT = 3;

const TIMEZONE_SYNC_RETRY_BASE_MS = 500;
const TIMEZONE_SYNC_RETRY_MAX_MS = 2_000;

export function timezoneSyncAttempt(attempt: number): 'send' | 'stop' {
  return attempt < TIMEZONE_SYNC_ATTEMPT_LIMIT ? 'send' : 'stop';
}

/**
 * Wait before another attempt after a rejection. A resolved false is not a
 * rejection: the server left the profile unchanged, and another call will too.
 */
export function timezoneSyncRetryDelayMs(attempt: number): number {
  return Math.min(
    TIMEZONE_SYNC_RETRY_MAX_MS,
    TIMEZONE_SYNC_RETRY_BASE_MS * 2 ** attempt
  );
}

export function detectBrowserTimezone(): string | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    return timezone && isValidIanaTimezone(timezone) ? timezone : null;
  } catch {
    return null;
  }
}

export function isValidIanaTimezone(value: string): boolean {
  const timezone = value.trim();
  if (!timezone) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

export function areEquivalentTimezones(left: string, right: string): boolean {
  try {
    const knownAliases: Record<string, string> = {
      'asia/calcutta': 'asia/kolkata',
      'etc/utc': 'utc',
      'etc/gmt': 'utc',
    };
    const canonicalize = (timezone: string) => {
      const resolved = new Intl.DateTimeFormat('en', { timeZone: timezone })
        .resolvedOptions()
        .timeZone.toLowerCase();
      return knownAliases[resolved] ?? resolved;
    };
    return canonicalize(left) === canonicalize(right);
  } catch {
    return false;
  }
}

export function resolveInitialTimezone(
  persistedTimezone: string | null | undefined,
  browserTimezone: string | null
): string {
  const persisted = persistedTimezone?.trim();
  // The form shows the stored zone, including the UTC signup default and a
  // device or phone zone. Substituting the live device zone makes the field
  // dirty, and saving it records that zone as a user choice.
  if (persisted && isValidIanaTimezone(persisted)) return persisted;
  if (browserTimezone && isValidIanaTimezone(browserTimezone)) {
    return browserTimezone;
  }
  return 'UTC';
}

/**
 * The zone a language-only save must send. The device zone is saved by
 * syncDetectedTimezone. Sending the browser zone through the language save
 * records it as a user choice and then blocks later device updates.
 */
export function timezoneForLanguageSave(args: {
  persistedTimezone: string | null | undefined;
}): string {
  const persisted = args.persistedTimezone?.trim();
  if (persisted && isValidIanaTimezone(persisted)) return persisted;
  return 'UTC';
}

/**
 * Whether the web app should report the browser zone (syncDetectedTimezone):
 * never over a chosen zone, nor over a legacy source-less non-UTC zone, and
 * not when the profile already holds this browser zone. Mirrors the server's
 * inferredTimezonePatch so the client doesn't send no-op writes.
 */
export function shouldSyncBrowserTimezone(
  settings: { timezone?: string; timezoneSource?: TimezoneSource },
  browserTimezone: string
): boolean {
  if (areEquivalentTimezones(browserTimezone, 'UTC')) return false;
  const source = settings.timezoneSource;
  const saved = settings.timezone?.trim() ?? '';
  if (source === 'user') return false;
  if (source === undefined && saved && !areEquivalentTimezones(saved, 'UTC')) {
    return false;
  }
  return !(
    source === 'browser' && areEquivalentTimezones(saved, browserTimezone)
  );
}
